/*
 * Desktop "Sort by" dropdown on collection and search pages
 * (snippets/facet-short.liquid). It only replaces the look of the native
 * select: picking an option sets the hidden select and fires its 'change'
 * event, so the theme's existing sort logic (FacetShort in assets/facets.js)
 * still does the sorting, URL update and AJAX render.
 *
 * - Click or Enter / Space / Arrow keys on the button open the list.
 * - Arrows, Home and End move; Enter / Space or a click picks.
 * - Esc, Tab or a click outside closes it.
 */
(function () {
  if (!window.customElements || customElements.get('sort-dropdown')) return;

  customElements.define(
    'sort-dropdown',
    class SortDropdown extends HTMLElement {
      connectedCallback() {
        this.select = document.getElementById(this.dataset.select);
        this.trigger = this.querySelector('[data-trigger]');
        this.panel = this.querySelector('[data-panel]');
        this.valueEl = this.querySelector('.sort-dd__value');
        this.options = Array.prototype.slice.call(this.querySelectorAll('[role="option"]'));
        this.activeIndex = 0;

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.trigger.addEventListener('click', this.toggle.bind(this));
        this.trigger.addEventListener('keydown', this.onTriggerKeydown.bind(this));
        this.panel.addEventListener('keydown', this.onPanelKeydown.bind(this));
        this.panel.addEventListener('click', this.onPanelClick.bind(this));
        this.panel.addEventListener('mousemove', this.onPanelHover.bind(this));
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick, true);
      }

      get isOpen() {
        return !this.panel.hidden;
      }

      toggle() {
        if (this.isOpen) this.close(true);
        else this.open();
      }

      open() {
        var selected = this.options.findIndex(function (option) {
          return option.getAttribute('aria-selected') === 'true';
        });
        this.panel.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        this.setActive(selected < 0 ? 0 : selected);
        // Next frame, so the panel fades / slides in.
        requestAnimationFrame(
          function () {
            this.classList.add('is-open');
          }.bind(this)
        );
        this.panel.focus({ preventScroll: true });
        document.addEventListener('click', this.onDocumentClick, true);
      }

      close(returnFocus) {
        if (!this.isOpen) return;
        this.classList.remove('is-open');
        this.panel.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        this.panel.removeAttribute('aria-activedescendant');
        document.removeEventListener('click', this.onDocumentClick, true);
        if (returnFocus) this.trigger.focus({ preventScroll: true });
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) this.close(false);
      }

      setActive(index) {
        this.activeIndex = index;
        this.options.forEach(function (option, i) {
          option.classList.toggle('is-active', i === index);
        });
        this.panel.setAttribute('aria-activedescendant', this.options[index].id);
      }

      onTriggerKeydown(event) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.open();
        }
      }

      onPanelKeydown(event) {
        var last = this.options.length - 1;
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            this.setActive(Math.min(this.activeIndex + 1, last));
            break;
          case 'ArrowUp':
            event.preventDefault();
            this.setActive(Math.max(this.activeIndex - 1, 0));
            break;
          case 'Home':
            event.preventDefault();
            this.setActive(0);
            break;
          case 'End':
            event.preventDefault();
            this.setActive(last);
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            this.pick(this.options[this.activeIndex]);
            break;
          case 'Escape':
            event.preventDefault();
            this.close(true);
            break;
          case 'Tab':
            this.close(false);
            break;
        }
      }

      onPanelClick(event) {
        var option = event.target.closest('[role="option"]');
        if (option) this.pick(option);
      }

      onPanelHover(event) {
        var option = event.target.closest('[role="option"]');
        if (option) this.setActive(this.options.indexOf(option));
      }

      pick(option) {
        this.close(true);
        if (option.getAttribute('aria-selected') === 'true') return;

        this.options.forEach(function (item) {
          item.setAttribute('aria-selected', String(item === option));
        });
        this.valueEl.textContent = option.textContent.trim();

        if (!this.select) return;
        this.select.value = option.dataset.value;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  );
})();
